import React, { useState, useEffect } from "react";
import {
  TextField,
  IconButton,
  InputAdornment,
  Skeleton,
  Box,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UseProductSearchPanel from "./useProductSearchPanel";

interface CustomSearchBarProps {
  onSearch: (searchTerm: string) => void;
  placeholder?: string;
}

const ProductSearchPanel: React.FC<CustomSearchBarProps> = ({
  onSearch,
  placeholder = "Search by products...",
}) => {
  const { selectCategoryList, SearchFilter, formContext } =
    UseProductSearchPanel();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true); // New state for loading

  const { handleSubmit, register, setValue } = formContext;

  // Simulate data loading delay
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  }, []);

  const handleSearch = () => {
    onSearch(searchTerm);
  };

  const handleClear = () => {
    setSearchTerm("");
    onSearch("");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setValue("SearchText", value);
  };

  const handleCategoryChange = (event: any) => {
    const categoryId = Number(event.target.value) || 0;
    setSelectedCategory(categoryId);
    setValue("ProductCategoryMasterID", categoryId);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <form onSubmit={handleSubmit(SearchFilter)}>
      <Box
        sx={{
          background: "linear-gradient(90deg, #1c1c1c, #2c2c2c)",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          padding: {
            xs: "12px 5px", // mobile
            sm: "12px 5px", // tablet
            md: "6px 20px", // small laptop
            lg: "6px 20px", // desktop
          },
        }}
      >
        {/* Category Dropdown */}
        <Box
          sx={{
            display: {
              xs: "none",
              sm: "none",
              md: "block",
              lg: "block",
              xl: "block",
            },
          }}
        >
          <Select
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="standard"
            disableUnderline
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              color: "#fff",
              minWidth: "140px",
              "& .MuiSelect-icon": { color: "#fff" },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#000",
                  color: "#fff",
                  "& .MuiMenuItem-root": {
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value={0}>All Categories</MenuItem>
            {selectCategoryList.map((category: any) => (
              <MenuItem
                key={category.ProductCategoryMasterID}
                value={category.ProductCategoryMasterID}
              >
                {category.ProductCategoryName}
              </MenuItem>
            ))}
          </Select>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 2, backgroundColor: "#555" }}
          />
        </Box>

        {isLoading ? (
          <Skeleton
            variant="rectangular"
            height={56}
            animation="wave"
            sx={{ borderRadius: "18px" }}
          />
        ) : (
          <TextField
            {...register("SearchText")}
            size="small"
            fullWidth
            variant="outlined"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            sx={{
              width: "100%",
              // bgcolor: "#eff1f5",
              //background: "linear-gradient(90deg, #1c1c1c, #2c2c2c)",
              borderRadius: "8px",

              // Input text color
              "& .MuiOutlinedInput-input": {
                color: "#fff",
                padding: 0,
              },

              // Placeholder color
              "& .MuiOutlinedInput-input::placeholder": {
                color: "#ccc",
                opacity: 1, // Important (default MUI reduces opacity)
              },

              "& .MuiInputLabel-root": {
                color: "#fff",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "transparent",
                  borderRadius: "8px",
                },
                "&:hover fieldset": {
                  borderColor: "transparent",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "transparent",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{ ml: "-10px", mr: "-1px" }}
                >
                  <IconButton
                  type="submit"
                    onClick={handleSearch}
                    aria-label="Search by products"
                    sx={{ color: "#ccc" }}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm ? (
                    <IconButton onClick={handleClear} aria-label="clear">
                      <ClearIcon />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>
    </form>
  );
};

export default ProductSearchPanel;
